import { docsConsoleDocumentsIntro, docsConsoleDocumentsTitle } from 'Clepit/translations';
import type { SupportedLanguage } from '@bomdisoft/translations';
import type { EditorData } from '@clepit/core';

/** Derived from `app/[lang]/pages/[id]/*` (screen, doc-header, doc-menu, panel,
 * comments-panel, contents-panel, history-panel, save-badge, metadata, editor). */
export const buildConsoleDocuments = (language: SupportedLanguage): EditorData => ({
  blocks: [
    { data: { html: docsConsoleDocumentsTitle[language], level: 'h1' as const }, type: 'header' },
    { data: { html: docsConsoleDocumentsIntro[language] }, type: 'paragraph' },

    { data: { alt: 'A document open in the console editor', caption: 'A document in the editor, with the save badge and header actions.', url: '/images/docs/console/documents/screen-light.png' }, type: 'image' },
    { data: { alt: 'A document open in the console editor, dark theme', caption: 'A document in the editor, with the save badge and header actions.', url: '/images/docs/console/documents/screen-dark.png' }, type: 'image' },

    { data: { html: 'The header', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'Back and forward, the breadcrumb trail, and the document actions: favourite it, open its info, share it, and the document menu. Info holds the reading insights: views, readers, followers, and when it was last read, and it says plainly when insights are unavailable or the reader is not allowed to see them.',
      },
      type: 'paragraph',
    },

    { data: { html: 'Writing', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'The body is the Clepit editor itself, so every block type, inline tool, and slash command in the <a href="../blocks">block reference</a> and <a href="../inline-tools">inline tools reference</a> is available here. The document menu adds what belongs to the document rather than a block: a description, tags, and export to HTML or Markdown.',
      },
      type: 'paragraph',
    },

    { data: { html: 'Saving, offline, and conflicts', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'The save badge in the header tells the truth about persistence: saved, saving, a retry when a save failed, and the reason when the console is offline. When another session changed the document underneath yours, the badge becomes a conflict notice with a reload; restoring an old revision that lost a race surfaces the same conflict copy, so there is one conflict story to learn, not two.',
      },
      type: 'paragraph',
    },

    { data: { html: 'The panels', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'Four switchers open panels beside the document. Contents is the table of contents built from the document headings, and says so when there are none. Comments carries the discussion with open, resolved, and unread segments and a composer. History lists revisions newest first with who and when, milestone stars, rename, restore, and load-more; Ask holds its place and says it is coming rather than pretending.',
      },
      type: 'paragraph',
    },
    { data: { alt: 'The contents panel open beside a document', caption: 'The contents panel, following the document headings.', url: '/images/docs/console/documents/contents-light.png' }, type: 'image' },
    { data: { alt: 'The contents panel open beside a document, dark theme', caption: 'The contents panel, following the document headings.', url: '/images/docs/console/documents/contents-dark.png' }, type: 'image' },

    { data: { html: 'Where this meets the docs', level: 'h2' as const }, type: 'header' },
    {
      data: {
        html: 'Publishing and share links live behind the Share button and are covered in <a href="./sharing">Publishing &amp; sharing</a>. The block payloads the editor writes are the JSON documented in the <a href="../blocks">block reference</a>, and the same document renders anywhere through <a href="../embedding">rendering and embedding</a>.',
      },
      type: 'paragraph',
    },
  ],
  time: 1_745_000_000_000,
  version: '1.0.0',
});
